<template>
  <div class="vip-upgrade-container">
    <div class="header-section">
      <div class="header-bg"></div>
      <van-nav-bar 
        title="升级VIP" 
        left-arrow 
        @click-left="goBack"
        :border="false"
        class="nav-bar"
      />
      <div class="header-content">
        <div class="current-vip-card">
          <div class="vip-badge" :class="'vip-' + user.vip_level">
            <div class="badge-icon">
              <van-icon :name="vipIcon" size="36" color="#fff" />
            </div>
            <div class="badge-info">
              <h3>{{ vipText }}</h3>
              <p v-if="user.vip_level > 0">{{ vipExpireText }}</p>
              <p v-else class="no-vip-tip">立即升级，解锁更多特权</p>
            </div>
          </div>
          <div class="current-vip-info">
            <div class="info-item">
              <span class="label">当前等级</span>
              <span class="value">{{ vipText }}</span>
            </div>
            <div class="info-divider"></div>
            <div class="info-item" v-if="user.vip_level > 0">
              <span class="label">剩余天数</span>
              <span class="value">{{ daysLeft }}天</span>
            </div>
            <div class="info-item" v-else>
              <span class="label">会员状态</span>
              <span class="value">未开通</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="content">
      <div class="upgrade-tips">
        <div class="tips-card">
          <van-icon name="sparkles" size="18" color="#f59e0b" />
          <span>升级VIP后，您将享受更多特权和专属服务</span>
        </div>
      </div>

      <div class="section-header">
        <h2 class="section-title">选择升级套餐</h2>
        <span class="section-count">{{ availablePackages.length }}个套餐可选</span>
      </div>

      <div v-if="loading" class="loading-wrapper">
        <van-loading size="32px" vertical>
          <span class="loading-text">加载套餐中...</span>
        </van-loading>
      </div>
      
      <div v-if="error" class="empty-wrapper">
        <div class="empty-illustration error">
          <van-icon name="warning-o" class="empty-icon" />
        </div>
        <h3 class="empty-title">加载套餐失败</h3>
        <p class="empty-desc">{{ error }}</p>
        <van-button round type="danger" size="small" @click="loadPackages">重新加载</van-button>
      </div>
      
      <div v-if="!loading && !error && availablePackages.length === 0 && packages.length === 0" class="empty-wrapper">
        <div class="empty-illustration">
          <van-icon name="gift-o" class="empty-icon" />
        </div>
        <h3 class="empty-title">暂无VIP套餐</h3>
        <p class="empty-desc">系统暂无VIP套餐数据</p>
        <van-button round type="primary" size="small" @click="initPackages">初始化套餐</van-button>
      </div>
      
      <div v-if="!loading && !error && availablePackages.length === 0" class="empty-wrapper">
        <div class="empty-illustration success" v-if="user.vip_level >= 4">
          <van-icon name="crown" class="empty-icon" />
        </div>
        <div class="empty-illustration" v-else>
          <van-icon name="star-o" class="empty-icon" />
        </div>
        <h3 class="empty-title">{{ user.vip_level >= 4 ? '恭喜您！' : '暂无可升级套餐' }}</h3>
        <p class="empty-desc">{{ user.vip_level >= 4 ? '您已经是最高等级VIP，无需升级' : '当前没有可升级的套餐' }}</p>
      </div>

      <div class="packages" v-if="!loading && !error && availablePackages.length > 0">
        <div 
          v-for="(pkg, index) in availablePackages" 
          :key="pkg.id"
          class="package-card"
          :class="{ 
            'selected': selectedPackage === pkg.id,
            'vip-2': pkg.level === 2,
            'vip-3': pkg.level === 3,
            'vip-4': pkg.level === 4
          }"
          :style="{ animationDelay: index * 0.1 + 's' }"
          @click="selectPackage(pkg)"
        >
          <div class="card-accent" :class="'accent-vip-' + pkg.level"></div>
          
          <div class="package-header">
            <div class="package-icon" :class="'icon-vip-' + pkg.level">
              <van-icon :name="pkg.icon" size="22" color="#fff" />
            </div>
            <div class="package-info">
              <span class="package-name">{{ pkg.name }}</span>
              <span class="package-bank">{{ pkg.bank_label || pkg.bank_name || '未关联题库' }}</span>
              <span class="package-level">套餐等级 VIP{{ pkg.level }}</span>
            </div>
            <!-- <div class="upgrade-tag">升级</div> -->
          </div>
          
          <div class="package-body">
            <div class="price-section">
              <span class="currency">¥</span>
              <span class="price">{{ parseFloat(pkg.price).toFixed(2) }}</span>
              <span v-if="pkg.original_price && pkg.original_price > pkg.price" class="original-price">
                ¥{{ parseFloat(pkg.original_price).toFixed(2) }}
              </span>
            </div>
            <div class="package-days">
              <van-icon name="calendar" size="14" />
              <span>{{ pkg.valid_type === 2 ? '永久有效' : pkg.days + '天有效' }}</span>
            </div>
            <div class="package-features">
              <div class="feature-item">
                <van-icon name="check-circle" size="14" />
                <span>无限答题</span>
              </div>
              <div class="feature-item" v-if="pkg.level >= 2">
                <van-icon name="check-circle" size="14" />
                <span>专属题目</span>
              </div>
              <div class="feature-item" v-if="pkg.level >= 3">
                <van-icon name="check-circle" size="14" />
                <span>优先评分</span>
              </div>
              <div class="feature-item" v-if="pkg.level >= 4">
                <van-icon name="check-circle" size="14" />
                <span>专属客服</span>
              </div>
            </div>
          </div>
          
          <div class="select-indicator" v-if="selectedPackage === pkg.id">
            <van-icon name="checked" size="16" />
          </div>
        </div>
      </div>

      <div class="upgrade-benefits" v-if="selectedPackage">
        <div class="benefits-header">
          <van-icon name="gift" size="20" />
          <h3>升级后特权</h3>
        </div>
        <div class="benefit-item">
          <div class="benefit-check">
            <van-icon name="passed" size="16" />
          </div>
          <span>从 {{ vipText }} 升级到 {{ selectedPackageSummary || selectedPackageName }}</span>
        </div>
        <div class="benefit-item">
          <div class="benefit-check">
            <van-icon name="passed" size="16" />
          </div>
          <span>解锁更多专属题目</span>
        </div>
        <div class="benefit-item">
          <div class="benefit-check">
            <van-icon name="passed" size="16" />
          </div>
          <span>享受更高级的VIP服务</span>
        </div>
        <div class="benefit-item">
          <div class="benefit-check">
            <van-icon name="passed" size="16" />
          </div>
          <span>延长VIP有效期</span>
        </div>
      </div>
    </div>

    <div class="footer">
      <div class="footer-info" v-if="selectedPackage">
        <span class="footer-label">选择套餐</span>
        <span class="footer-value">{{ selectedPackageSummary }}</span>
      </div>
      <van-button 
        type="primary" 
        block 
        round
        :disabled="!selectedPackage"
        :loading="paying"
        class="upgrade-btn"
        @click="handleUpgrade"
      >
        {{ paying ? '升级中...' : '立即升级' }}
      </van-button>
    </div>
  </div>
</template>

<script>
import { Toast } from 'vant'
import { syncOrderPayAfterWxSuccess } from '@/utils/wxpay'
import { resolveUserVipDisplayName } from '@/utils/vipDisplay'

export default {
  name: 'VipUpgrade',
  data() {
    return {
      packages: [],
      selectedPackage: null,
      paying: false,
      loading: false,
      error: null
    }
  },
  computed: {
    user() {
      return this.$store.state.user || {}
    },
    vipText() {
      return resolveUserVipDisplayName(this.user)
    },
    vipIcon() {
      const icons = {
        0: 'user-circle-o',
        1: 'star-o',
        2: 'fire-o',
        3: 'gem-o',
        4: 'diamond-o'
      }
      return icons[this.user.vip_level] || 'user-circle-o'
    },
    vipExpireText() {
      if (!this.user.vip_end_time || this.user.vip_end_time === 0) {
        return this.user.vip_level > 0 ? '永久有效' : '未开通VIP'
      }
      const expireDate = new Date(this.user.vip_end_time * 1000)
      const year = expireDate.getFullYear()
      const month = (expireDate.getMonth() + 1).toString().padStart(2, '0')
      const day = expireDate.getDate().toString().padStart(2, '0')
      return `有效期至: ${year}-${month}-${day}`
    },
    daysLeft() {
      if (this.user.vip_end_time === 0 || this.user.vip_end_time === 2147483647) return '永久'
      const days = Math.ceil((this.user.vip_end_time - Math.floor(Date.now() / 1000)) / 86400)
      return Math.max(0, days)
    },
    availablePackages() {
      const currentLevel = parseInt(this.user.vip_level, 10) || 0
      
      if (currentLevel >= 4) {
        return []
      }
      
      return this.packages.filter(pkg => {
        const pkgLevel = parseInt(pkg.level, 10)
        
        if (pkgLevel <= currentLevel) {
          return false
        }
        
        return true
      })
    },
    selectedPackageName() {
      if (!this.selectedPackage) return ''
      const pkg = this.packages.find(p => p.id === this.selectedPackage)
      return pkg ? pkg.name : ''
    },
    selectedPackageSummary() {
      if (!this.selectedPackage) return ''
      const pkg = this.packages.find(p => p.id === this.selectedPackage)
      if (!pkg) return ''
      const bank = pkg.bank_label || pkg.bank_name
      return bank ? `${bank} · ${pkg.name}` : pkg.name
    }
  },
  async mounted() {
    await this.loadPackages()
  },
  methods: {
    defaultFeaturesForLevel(level) {
      const lv = parseInt(level, 10) || 0
      const list = ['无限答题']
      if (lv >= 2) list.push('专属题目')
      if (lv >= 3) list.push('优先评分')
      if (lv >= 4) list.push('专属客服')
      return list
    },
    goBack() {
      const backRoute = this.$store.getters.smartBackRoute
      this.$router.push(backRoute)
    },
    async loadPackages() {
      this.loading = true
      this.error = null
      
      try {
        const response = await this.$http.get('/api/vip/packages')
        
        console.log('VIP套餐API响应:', response.data)
        
        const packages = response.data.data.packages || []
        console.log('VIP套餐数据:', packages)
        
        const byLevel = {}
        packages.forEach(pkg => {
          if (parseInt(pkg.status, 10) !== 1) return
          const level = parseInt(pkg.level, 10)
          if (level < 1) return
          const sort = parseInt(pkg.sort, 10) || level
          const id = parseInt(pkg.id, 10)
          const prev = byLevel[level]
          if (!prev || sort > prev._sort || (sort === prev._sort && id > prev.id)) {
            byLevel[level] = { pkg, _sort: sort, id }
          }
        })
        this.packages = Object.keys(byLevel)
          .map(k => parseInt(k, 10))
          .sort((a, b) => a - b)
          .map(level => {
            const pkg = byLevel[level].pkg
            return {
              id: pkg.id,
              name: pkg.name,
              level,
              bank_id: pkg.bank_id,
              bank_name: pkg.bank_name,
              bank_level: pkg.bank_level,
              bank_label: pkg.bank_label,
              days: pkg.valid_days,
              price: pkg.price,
              original_price: pkg.original_price,
              color: pkg.color || '#52c41a',
              icon: pkg.icon || 'star-o',
              valid_type: pkg.valid_type,
              sort: pkg.sort,
              features: Array.isArray(pkg.features) ? pkg.features : []
            }
          })
        
        console.log('处理后的套餐:', this.packages)
        console.log('当前用户VIP等级:', this.user.vip_level)
        console.log('可升级的套餐:', this.availablePackages)
        
        this.loading = false
      } catch (err) {
        console.error('加载VIP套餐失败:', err)
        this.error = err.message || '加载套餐失败'
        this.loading = false
        Toast.fail(this.error)
      }
    },
    async initPackages() {
      try {
        const response = await this.$http.post('/api/vip/packages/init')
        Toast.success('VIP套餐初始化成功')
        await this.loadPackages()
      } catch (err) {
        Toast.fail(err.message || '初始化失败')
      }
    },
    selectPackage(pkg) {
      this.selectedPackage = pkg.id
    },
    async handleUpgrade() {
      if (!this.selectedPackage) {
        Toast('请选择升级套餐')
        return
      }

      this.paying = true
      
      try {
        const response = await this.$http.post('/api/vip/buy', {
          package_id: this.selectedPackage
        })
        
        const orderNo = response.data.data.order_no
        const jsApiParams = response.data.data.jsApiParams
        
        if (typeof WeixinJSBridge !== 'undefined') {
          WeixinJSBridge.invoke('getBrandWCPayRequest', jsApiParams, async (res) => {
            if (res.err_msg === 'get_brand_wcpay_request:ok') {
              Toast.loading({ message: '正在开通会员…', forbidClick: true, duration: 0 })
              await syncOrderPayAfterWxSuccess(this.$http, orderNo)
              Toast.clear()
              this.paying = false
              Toast.success('升级成功')
              await this.$store.dispatch('fetchUserInfo')
              await this.$store.dispatch('fetchQuestionBanks')
              setTimeout(() => {
                this.$router.back()
              }, 1200)
            } else {
              this.paying = false
              Toast.fail('升级失败')
            }
          })
        } else {
          Toast('请在微信中打开')
          this.paying = false
        }
      } catch (err) {
        this.paying = false
        Toast.fail(err.message)
      }
    }
  }
}
</script>

<style scoped>
::v-deep .van-nav-bar__title{
  color: white;
}
::v-deep .van-nav-bar .van-icon {
  color: white;
}
.vip-upgrade-container {
  min-height: 100vh;
  background: #f5f6fa;
  padding-bottom: 120px;
}

.header-section {
  position: relative;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding-bottom: 32px;
  border-radius: 0 0 32px 32px;
  overflow: hidden;
}

.header-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%);
  backdrop-filter: blur(10px);
}

.nav-bar {
  position: relative;
  z-index: 1;
  background: transparent;
}

.nav-bar .van-nav-bar__title {
  color: white;
  font-weight: 600;
  font-size: 18px;
}

.nav-bar .van-nav-bar__left,
.nav-bar .van-nav-bar__right {
  color: white;
}

.header-content {
  position: relative;
  z-index: 1;
  padding: 0 20px;
  margin-top: 12px;
}

.current-vip-card {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.vip-badge {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}

.badge-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.25);
}

.vip-badge.vip-0 .badge-icon {
  background: rgba(153, 153, 153, 0.3);
}

.vip-badge.vip-1 .badge-icon {
  background: rgba(24, 144, 255, 0.4);
}

.vip-badge.vip-2 .badge-icon {
  background: rgba(47, 84, 235, 0.4);
}

.vip-badge.vip-3 .badge-icon {
  background: rgba(114, 46, 209, 0.4);
}

.vip-badge.vip-4 .badge-icon {
  background: rgba(250, 140, 22, 0.4);
}

.badge-info {
  flex: 1;
}

.badge-info h3 {
  font-size: 20px;
  font-weight: 600;
  color: white;
  margin-bottom: 6px;
}

.badge-info p {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
}

.no-vip-tip {
  color: rgba(255, 255, 255, 0.7) !important;
}

.current-vip-info {
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.info-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.info-item .label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.75);
  font-weight: 500;
}

.info-item .value {
  font-size: 17px;
  font-weight: 700;
  color: white;
}

.info-divider {
  width: 1px;
  height: 32px;
  background: rgba(255, 255, 255, 0.2);
}

.content {
  padding: 24px 16px;
  margin-top: -24px;
}

.upgrade-tips {
  margin-bottom: 20px;
}

.tips-card {
  display: flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border-radius: 16px;
  padding: 14px 16px;
  border: 1px solid #fde68a;
}

.tips-card span {
  font-size: 14px;
  color: #92400e;
  font-weight: 500;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a2e;
}

.section-count {
  font-size: 13px;
  color: #999;
  padding: 4px 10px;
  background: #f5f6fa;
  border-radius: 12px;
}

.loading-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
}

.loading-text {
  margin-top: 12px;
  font-size: 14px;
  color: #666;
}

.empty-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  padding: 40px 20px;
  text-align: center;
}

.empty-illustration {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea20, #764ba220);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.15);
}

.empty-illustration.error {
  background: linear-gradient(135deg, #fee2e2, #fecaca);
}

.empty-illustration.success {
  background: linear-gradient(135deg, #dcfce7, #bbf7d0);
}

.empty-icon {
  font-size: 56px;
  color: #667eea;
}

.empty-illustration.error .empty-icon {
  color: #ef4444;
}

.empty-illustration.success .empty-icon {
  color: #22c55e;
}

.empty-title {
  font-size: 20px;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 8px;
}

.empty-desc {
  font-size: 14px;
  color: #666;
  margin-bottom: 32px;
}

.packages {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}

.package-card {
  position: relative;
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: slideUp 0.4s ease-out forwards;
  opacity: 0;
  transform: translateY(20px);
}

@keyframes slideUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.package-card.selected {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(102, 126, 234, 0.18);
}

.card-accent {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
}

.accent-vip-2 {
  background: linear-gradient(180deg, #2f54eb, #3b82f6);
}

.accent-vip-3 {
  background: linear-gradient(180deg, #722ed1, #a855f7);
}

.accent-vip-4 {
  background: linear-gradient(180deg, #fa8c16, #fbbf24);
}

.package-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: #fafafa;
}

.package-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-vip-2 {
  background: linear-gradient(135deg, #2f54eb, #3b82f6);
}

.icon-vip-3 {
  background: linear-gradient(135deg, #722ed1, #a855f7);
}

.icon-vip-4 {
  background: linear-gradient(135deg, #fa8c16, #fbbf24);
}

.package-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.package-name {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a2e;
}

.package-bank {
  font-size: 12px;
  color: #667eea;
  font-weight: 500;
}

.package-level {
  font-size: 12px;
  color: #999;
}

.upgrade-tag {
  padding: 4px 10px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  font-size: 12px;
  font-weight: 600;
  border-radius: 12px;
}

.package-body {
  padding: 20px;
}

.price-section {
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 12px;
}

.currency {
  font-size: 18px;
  font-weight: 600;
  color: #667eea;
}

.price {
  font-size: 40px;
  font-weight: 700;
  color: #667eea;
}

.original-price {
  font-size: 14px;
  color: #999;
  text-decoration: line-through;
  margin-left: 8px;
}

.package-days {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #666;
  margin-bottom: 16px;
}

.package-features {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: #f5f6fa;
  border-radius: 8px;
  font-size: 12px;
  color: #4a5568;
}

.feature-item .van-icon {
  color: #22c55e;
}

.select-indicator {
  position: absolute;
  right: 16px;
  top: 16px;
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.upgrade-benefits {
  background: white;
  border-radius: 20px;
  padding: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  margin-bottom: 5rem;
}

.benefits-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.benefits-header h3 {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0;
}

.benefits-header .van-icon {
  color: #f59e0b;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.benefit-item:last-child {
  border-bottom: none;
}

.benefit-check {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(135deg, #dcfce7, #bbf7d0);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.benefit-check .van-icon {
  color: #22c55e;
}

.benefit-item span {
  font-size: 14px;
  color: #4a5568;
  line-height: 1.6;
}

.footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 20px;
  padding-bottom: calc(96px + env(safe-area-inset-bottom));
  background: white;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.08);
}

.footer-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.footer-label {
  font-size: 13px;
  color: #999;
}

.footer-value {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
}

.upgrade-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);
  font-weight: 600;
  height: 48px;
}

.upgrade-btn:not(:disabled):active {
  transform: scale(0.98);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}
</style>
